import React from 'react';
import { View, StyleSheet, Pressable, Image, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from './Text';
import { Theme } from '../../utils/theme';
import { placeholders } from '../../assets/placeholders';

export interface BookCardProps {
  /** Book title */
  title: string;
  /** Book author */
  author?: string;
  /** Cover image URL */
  coverUrl?: string;
  /** Number of ideas/insights */
  insightsCount?: number;
  /** Custom action label (e.g., "4 ideas from this book chosen for you") - overrides insightsCount display */
  actionLabel?: string;
  /** Mentor note/reason */
  mentorNote?: string;
  /** Position/rank badge (e.g. 1, 2, 3) */
  position?: number;
  /** Card variant */
  variant?: 'compact' | 'grid' | 'carousel' | 'vertical';
  /** Press handler */
  onPress?: () => void;
  /** Additional styles */
  style?: ViewStyle;
  /** Children (for AI message in vertical variant) */
  children?: React.ReactNode;
}

/**
 * Shared book card component with multiple variants:
 * - compact: row layout with small cover (Home, Saved)
 * - grid: tile layout (Explore grid)
 * - carousel: horizontal scrollable item (Explore carousels)
 * - vertical: full-width layout with larger cover (Dashboard recommendations)
 */
const BookCard: React.FC<BookCardProps> = ({
  title,
  author,
  coverUrl,
  insightsCount,
  actionLabel,
  mentorNote,
  position,
  variant = 'compact',
  onPress,
  style,
  children,
}) => {
  const imageSource = coverUrl ? { uri: coverUrl } : placeholders.bookCover;
  const displayInsightsCount = insightsCount !== undefined && insightsCount > 0 ? insightsCount : undefined;

  if (variant === 'vertical') {
    return (
      <Pressable
        style={({ pressed }) => [
          styles.verticalContainer,
          pressed && styles.verticalPressed,
          style,
        ]}
        onPress={onPress}
      >
        <View style={styles.verticalContent}>
          {/* Book Cover */}
          <View style={styles.verticalCoverContainer}>
            <View style={styles.verticalCover}>
              <Image source={imageSource} style={styles.verticalImage} resizeMode="cover" />
            </View>
          </View>

          {/* Book Info */}
          <View style={styles.verticalInfo}>
            <Text style={styles.verticalTitle} numberOfLines={2}>
              {title}
            </Text>
            {author && (
              <Text style={styles.verticalAuthor} numberOfLines={1}>
                {author}
              </Text>
            )}
            {actionLabel ? (
              <Text style={styles.verticalActionLabel}>{actionLabel}</Text>
            ) : displayInsightsCount !== undefined ? (
              <Text style={styles.verticalInsights}>{displayInsightsCount} Ideas</Text>
            ) : null}
          </View>
        </View>

        {/* AI Personalized Message (passed as children) */}
        {children}
      </Pressable>
    );
  }

  if (variant === 'grid') {
    return (
      <Pressable
        style={({ pressed }) => [
          styles.gridContainer,
          pressed && styles.gridPressed,
          style,
        ]}
        onPress={onPress}
      >
        <View style={styles.gridCover}>
          <Image source={imageSource} style={styles.gridImage} resizeMode="cover" />
        </View>
        {author && <Text style={styles.gridAuthor}>{author}</Text>}
      </Pressable>
    );
  }

  if (variant === 'carousel') {
    return (
      <Pressable
        style={({ pressed }) => [
          styles.carouselContainer,
          pressed && styles.carouselPressed,
          style,
        ]}
        onPress={onPress}
      >
        <View style={styles.carouselCover}>
          <Image source={imageSource} style={styles.carouselImage} resizeMode="cover" />
        </View>
        {author && <Text style={styles.carouselAuthor}>{author}</Text>}
      </Pressable>
    );
  }

  // compact variant (default)
  return (
    <Pressable
      style={({ pressed }) => [
        styles.compactContainer,
        pressed && styles.compactPressed,
        style,
      ]}
      onPress={onPress}
    >
      <View style={styles.compactContent}>
        {/* Book Cover with Position Badge */}
        <View style={styles.compactCoverContainer}>
          <View style={styles.compactCover}>
            <Image source={imageSource} style={styles.compactImage} resizeMode="cover" />
          </View>
          {position !== undefined && (
            <View style={styles.positionBadge}>
              <Text style={styles.positionText}>{position}</Text>
            </View>
          )}
        </View>

        {/* Book Info */}
        <View style={styles.compactInfo}>
          <Text style={styles.compactTitle} numberOfLines={1}>
            {title}
          </Text>
          {mentorNote && (
            <Text style={styles.compactNote} numberOfLines={2}>
              {mentorNote}
            </Text>
          )}
          {displayInsightsCount !== undefined && (
            <View style={styles.compactMeta}>
              <Text style={styles.compactInsights}>{displayInsightsCount} Ideas</Text>
            </View>
          )}
        </View>

        {/* Right Chevron */}
        <View style={styles.compactChevron}>
          <Ionicons name="chevron-forward" size={16} color={Theme.colors.textSecondary} />
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  // Compact variant (row layout)
  compactContainer: {
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.lg,
    ...Theme.shadows.md,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  compactPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  compactContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
  },
  compactCoverContainer: {
    position: 'relative',
    flexShrink: 0,
  },
  compactCover: {
    width: 56,
    height: 80,
    borderRadius: Theme.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: Theme.colors.surfaceSecondary,
    ...Theme.shadows.bookCover,
    borderWidth: 0.5,
    borderColor: Theme.colors.borderLight,
  },
  compactImage: {
    width: '100%',
    height: '100%',
  },
  positionBadge: {
    position: 'absolute',
    bottom: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#224967',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 2,
  },
  positionText: {
    fontSize: 11,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: '#fff',
  },
  compactInfo: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    gap: 4,
  },
  compactTitle: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textPrimary,
  },
  compactNote: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    lineHeight: 18,
  },
  compactMeta: {
    marginTop: 4,
  },
  compactInsights: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.primary,
  },
  compactChevron: {
    justifyContent: 'center',
    marginTop: 4,
  },

  // Grid variant (tile layout)
  gridContainer: {
    width: 140,
  },
  gridPressed: {
    opacity: 0.7,
  },
  gridCover: {
    width: 140,
    aspectRatio: 2 / 3,
    borderRadius: Theme.borderRadius.lg,
    overflow: 'hidden',
    backgroundColor: Theme.colors.surfaceSecondary,
    ...Theme.shadows.bookCover,
    borderWidth: 0.5,
    borderColor: Theme.colors.borderLight,
    marginBottom: Theme.spacing.sm,
  },
  gridImage: {
    width: '100%',
    height: '100%',
  },
  gridAuthor: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },

  // Carousel variant (horizontal scroll item)
  carouselContainer: {
    width: 140,
  },
  carouselPressed: {
    opacity: 0.7,
  },
  carouselCover: {
    width: 140,
    aspectRatio: 2 / 3,
    borderRadius: Theme.borderRadius.lg,
    overflow: 'hidden',
    backgroundColor: Theme.colors.surfaceSecondary,
    ...Theme.shadows.bookCover,
    borderWidth: 0.5,
    borderColor: Theme.colors.borderLight,
    marginBottom: Theme.spacing.sm,
  },
  carouselImage: {
    width: '100%',
    height: '100%',
  },
  carouselAuthor: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },

  // Vertical variant (full-width dashboard card)
  verticalContainer: {
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.lg,
    ...Theme.shadows.md,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  verticalPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  verticalContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
  },
  verticalCoverContainer: {
    position: 'relative',
    flexShrink: 0,
  },
  verticalCover: {
    width: 80,
    height: 120,
    borderRadius: Theme.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: Theme.colors.surfaceSecondary,
    ...Theme.shadows.bookCover,
    borderWidth: 0.5,
    borderColor: Theme.colors.borderLight,
  },
  verticalImage: {
    width: '100%',
    height: '100%',
  },
  verticalInfo: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    gap: 6,
  },
  verticalTitle: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    lineHeight: 22,
  },
  verticalAuthor: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },
  verticalInsights: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.primary,
    marginTop: 4,
  },
  verticalActionLabel: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.accent600,
    marginTop: 4,
  },
});

export default BookCard;

